import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import LogoLoader from 'components/Loader/LogoLoader';
import APIErrorMascot from 'components/Mascot/APIErrorMascot';
import { lunchBreakDuration } from 'containers/TimesheetMileage/shared/options';
import TimesheetForm from 'containers/TimesheetMileage/shared/TimesheetForm';
import { initialTimesheetFormData } from 'containers/TimesheetMileage/shared/TimesheetForm/schema';
import { routePaths } from 'global/routePaths';
import { useFetchPaginatedCustomFields } from 'hooks/customField/useFetchPaginatedCustomFields';
import { useFetchTimesheetById } from 'hooks/timesheet/useFetchTimesheetById';
import { hasCustomFieldAccess, transformSavedCustomFieldsToFormData } from 'utils/customFieldsUtils';
import { getDistanceInCompanyFormat, secondsToMS, isNumber } from 'utils/helpers';
import toaster from 'utils/toaster';

const EditTimesheet: React.FC<unknown> = () => {
  const [formData, setFormData] = useState<typeof initialTimesheetFormData>(initialTimesheetFormData);
  const { id } = useParams();

  const { status, data } = useFetchTimesheetById(id!);
  const hasAccess = hasCustomFieldAccess();

  const {
    status: customFieldStatus,
    fetchStatus: customFieldFetchStatus,
    data: customFields,
  } = useFetchPaginatedCustomFields(
    {
      page: 1,
      page_size: 500,
      module: 'timesheets',
      active: '1',
    },
    {
      enabled: hasAccess,
    }
  );

  useEffect(() => {
    if (status === 'success') {
      if (data?.payload?.approved === 1) {
        toaster.info({
          description: 'This timesheet has already been approved. Updates are not allowed.',
        });
        return;
      }

      if (data?.payload?.is_pto === 1) {
        toaster.info({
          description: 'This timesheet is for PTO and can’t be updated.',
        });
        return;
      }

      const fetchedFormData = data?.payload;

      let customFieldsFormData = {};

      if (customFieldStatus === 'success' && customFields?.payload) {
        customFieldsFormData = transformSavedCustomFieldsToFormData(
          customFields?.payload,
          fetchedFormData?.custom_fields
        );
      }

      const updatedFormData = {
        ...initialTimesheetFormData,
        id: fetchedFormData.id,
        user_id: fetchedFormData.user_id
          ? {
              userId: fetchedFormData.user_id,
              firstName: fetchedFormData.first_name,
              lastName: fetchedFormData.last_name,
            }
          : '',
        job_id: fetchedFormData?.job_id ? { name: fetchedFormData.job_name, id: fetchedFormData.job_id } : '',
        task_id: fetchedFormData?.task_id ? { name: fetchedFormData.task_name, id: fetchedFormData.task_id } : '',
        start: fetchedFormData?.start ? new Date(fetchedFormData.start) : '',
        end: fetchedFormData?.end ? new Date(fetchedFormData.end) : '',
        breaks:
          Array.isArray(fetchedFormData?.breaks) && fetchedFormData?.breaks.length
            ? fetchedFormData.breaks.map((item: any) => {
                const { minutes, seconds } = secondsToMS(item?.duration_in_seconds ?? 0);

                return {
                  break_id: item?.id,
                  time_break_rule_id: item?.break_type || '',
                  duration: item?.duration_in_seconds || 0,
                  duration_min: minutes,
                  duration_sec: seconds,
                  // startTime: item?.startLocal ? format(new Date(item?.startLocal), 'HH:mm') : '',
                  startTime: item?.startLocal ? new Date(item?.startLocal) : '',
                  // endTime: item?.endLocal ? format(new Date(item?.endLocal), 'HH:mm') : '',
                  endTime: item?.endLocal ? new Date(item?.endLocal) : '',
                  setTime: item?.startLocal && item?.endLocal ? 'yes' : 'no',
                };
              })
            : [],
        mileage:
          fetchedFormData?.mileage && isNumber(fetchedFormData.mileage)
            ? getDistanceInCompanyFormat(fetchedFormData?.mileage).toFixed(2)
            : 0,
        images: [],
        images_preview: fetchedFormData?.attachment_files || [],
        lunch_break_duration: !isNaN(fetchedFormData?.lunch_break_duration)
          ? lunchBreakDuration.find(breakItem => breakItem.value === fetchedFormData?.lunch_break_duration)
          : '',
        notes: fetchedFormData?.notes ?? '',
        is_child_timesheet: fetchedFormData?.is_child_timesheet ?? false,
        ...customFieldsFormData,
      };

      setFormData(updatedFormData);
    }
  }, [status, data, customFieldStatus, customFields]);

  if (status === 'loading' || customFieldFetchStatus === 'fetching') return <LogoLoader />;
  if (status === 'error' || customFieldStatus === 'error') return <APIErrorMascot />;
  if (status === 'success' && (data?.payload?.approved === 1 || data?.payload?.is_pto === 1))
    return <Navigate replace to={routePaths.timesheetAndMileage.timesheetDetail.replace(':id', data.payload.id)} />;

  return <TimesheetForm formData={formData} customFieldsData={hasAccess ? customFields?.payload || [] : null} />;
};

export default EditTimesheet;
